import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { BaseEntityAdapter } from "@store/store-types";
import {
  removeMemberFromCompany,
  updateMemberRoleInWorkspace,
} from "@store/members/members-slice-thunk";
import {
  addMembersToTeam,
  createTeam,
  deleteTeam,
  fetchTeamAsMember,
  fetchTeamDetails,
  fetchTeamMembers,
  fetchTeams,
  removeMembersFromTeam,
  updateTeamDetails,
} from "@store/teams/teams-slice-thunk";
import { TeamState } from "@store/teams/teams-slice-types";
import { removeLocalMemberFromTeam } from "@store/teams/teams-slice-utils";
import { getMemberId } from "@utils/member-utils";

export const teamAdapter: BaseEntityAdapter<FaroCoreApiTypes.Team> =
  createEntityAdapter({
    selectId: (team) => team.id,
  });

export const initialState: TeamState = {
  ...teamAdapter.getInitialState(),
  selectedTeam: {
    id: null,
    groups: null,
    teamAsMember: null,
    members: {},
    membersSortBy: null,
    totalCount: 0,
  },
  fetching: {
    status: "uninitialized",
    isRemovingTeam: false,
    isRemovingTeamMember: false,
    isFetchingTeamMembers: "uninitialized",
    isFetchingTeamDetails: "uninitialized",
    hasFetchedTeams: false,
    isFetchingMoreMembers: false,
  },
  nextTeamMember: null,
  isDetailOpened: false,
};

const teamSlice = createSlice({
  name: "teams",
  initialState,
  reducers: {
    resetTeamState: () => initialState,
    openDetailPanel: (state) => {
      state.isDetailOpened = true;
    },
    closeDetailPanel: (state) => {
      state.isDetailOpened = false;
    },
    removeMembersByEmails: (state, action: PayloadAction<string[]>) => {
      const emailsToRemove = new Set(action.payload);

      Object.values(state.entities).forEach((team) => {
        team.sampleMembers = team.sampleMembers.filter(
          (member) => !emailsToRemove.has(member.userResponse.email)
        );
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeams.pending, (state) => {
        state.fetching.status = "pending";
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        state.fetching.status = "succeeded";
        teamAdapter.upsertMany(state, action.payload);

        // Set the flag to know if the teams have been fetched for the first time.
        if (!state.fetching.hasFetchedTeams) {
          state.fetching.hasFetchedTeams = true;
        }
      })
      .addCase(fetchTeams.rejected, (state) => {
        state.fetching.status = "rejected";
      })

      .addCase(createTeam.fulfilled, (state, action) => {
        teamAdapter.setOne(state, action.payload);
      })

      .addCase(addMembersToTeam.pending, (state) => {
        state.fetching.status = "pending";
      })
      .addCase(addMembersToTeam.fulfilled, (state, action) => {
        state.fetching.status = "succeeded";

        const selectedId = state.selectedTeam.id;
        const teamId = action.payload.teamId;
        const team = state.entities[teamId];
        team.memberCount += action.payload.data.length;

        for (const member of action.payload.data) {
          if (selectedId) {
            const id = getMemberId(member.userResponse);
            state.selectedTeam.members[id] = member;
          } else {
            team.sampleMembers.push(member);
          }
        }

        if (selectedId) {
          state.selectedTeam.totalCount += action.payload.data.length;
        }
      })
      .addCase(addMembersToTeam.rejected, (state) => {
        state.fetching.status = "rejected";
      })

      .addCase(fetchTeamDetails.pending, (state) => {
        state.fetching.isFetchingTeamDetails = "pending";
      })
      .addCase(fetchTeamDetails.fulfilled, (state, action) => {
        state.selectedTeam.id = action.payload.id;
        state.fetching.isFetchingTeamDetails = "succeeded";
        teamAdapter.setOne(state, action.payload);
      })
      .addCase(fetchTeamDetails.rejected, (state) => {
        state.fetching.isFetchingTeamDetails = "rejected";
      })

      .addCase(fetchTeamAsMember.pending, (state) => {
        state.fetching.status = "pending";
      })
      .addCase(fetchTeamAsMember.fulfilled, (state, action) => {
        state.selectedTeam.teamAsMember = action.payload;
        state.fetching.status = "succeeded";
      })
      .addCase(fetchTeamAsMember.rejected, (state) => {
        state.fetching.status = "rejected";
      })

      .addCase(updateTeamDetails.pending, (state) => {
        state.fetching.status = "pending";
      })
      .addCase(updateTeamDetails.fulfilled, (state, action) => {
        state.selectedTeam.id = action.payload.id;
        state.fetching.status = "succeeded";
        teamAdapter.updateOne(state, {
          id: action.payload.id,
          changes: action.payload,
        });
      })
      .addCase(updateTeamDetails.rejected, (state) => {
        state.fetching.status = "rejected";
      })

      .addCase(removeMembersFromTeam.pending, (state) => {
        state.fetching.isRemovingTeamMember = true;
      })
      .addCase(removeMembersFromTeam.fulfilled, (state, action) => {
        removeLocalMemberFromTeam(state, action.payload);
        state.fetching.isRemovingTeamMember = false;
      })
      .addCase(removeMembersFromTeam.rejected, (state) => {
        state.fetching.isRemovingTeamMember = false;
      })

      .addCase(deleteTeam.pending, (state) => {
        state.fetching.isRemovingTeam = true;
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        teamAdapter.removeOne(state, action.payload);
        state.fetching.isRemovingTeam = false;
      })
      .addCase(deleteTeam.rejected, (state) => {
        state.fetching.isRemovingTeam = false;
      })

      .addCase(fetchTeamMembers.pending, (state, action) => {
        // If nextTeamMember is set, this is a pagination fetch
        if (action.meta.arg.start) {
          state.fetching.isFetchingMoreMembers = true;
        }
        state.fetching.isFetchingTeamMembers = "pending";
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        // Clear previous members only if this is a non-pagination fetch (first load, sort change)
        if (!action.meta.arg.start) {
          state.selectedTeam.members = {};
          state.selectedTeam.membersSortBy = action.meta.arg.sortBy ?? null;
        }
        state.nextTeamMember = action.payload.next ?? null;
        state.selectedTeam.totalCount = action.payload.totalCount;
        for (const member of action.payload.members) {
          const id = getMemberId(member.userResponse);
          state.selectedTeam.members[id] = member;
        }
        state.fetching.isFetchingMoreMembers = false;
        state.fetching.isFetchingTeamMembers = "succeeded";
      })
      .addCase(fetchTeamMembers.rejected, (state, _action) => {
        state.fetching.isFetchingMoreMembers = false;
        state.fetching.isFetchingTeamMembers = "rejected";
      })

      .addCase(removeMemberFromCompany.fulfilled, (state, action) => {
        const memberId = action.payload.identity;
        if (state.selectedTeam.members[memberId]) {
          delete state.selectedTeam.members[memberId];
          state.selectedTeam.totalCount -= 1;
        }
      })

      .addCase(updateMemberRoleInWorkspace.fulfilled, (state, action) => {
        // Need this case to update team role while updating team role as workspace member from overview page
        if (
          state.selectedTeam.teamAsMember &&
          state.selectedTeam.teamAsMember.identity === action.payload.identity
        ) {
          state.selectedTeam.teamAsMember.role = action.payload.role;
        }
      });
  },
});

export const {
  resetTeamState,
  openDetailPanel,
  closeDetailPanel,
  removeMembersByEmails,
} = teamSlice.actions;

export const teamReducer = teamSlice.reducer;
